import React from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel } from '@mui/material';
import SignatureCapture from '../widgets/SignatureCapture';

const ProcedureQuestionnaireSection = ({ 
  section, 
  responses, 
  onResponseChange, 
  validationErrors,
  disabled = false
}) => {
  const renderQuestionItem = (item) => {
    const value = responses[item.linkId] || '';
    const hasError = validationErrors[item.linkId];

    const handleChange = (newValue) => {
      onResponseChange(item.linkId, newValue);
    };

    switch (item.type) {
      case 'group':
        return (
          <Box key={item.linkId} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {item.text}
            </Typography>
            <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'grey.300' }}>
              {item.item?.map(subItem => renderQuestionItem(subItem))}
            </Box>
          </Box>
        );

      case 'string':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={item.text}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              error={!!hasError}
              helperText={hasError}
              placeholder={item.placeholder}
              disabled={disabled}
              size="small"
            />
          </Box>
        );

      case 'text':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={item.text}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              error={!!hasError}
              helperText={hasError}
              disabled={disabled}
              size="small"
            />
          </Box>
        );

      case 'choice':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <FormControl fullWidth size="small" error={!!hasError}>
              <InputLabel>{item.text}</InputLabel>
              <Select
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                label={item.text}
                disabled={disabled}
              >
                {item.options?.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {hasError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {hasError}
                </Typography>
              )}
            </FormControl>
          </Box>
        );

      case 'boolean':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === 'true'}
                  onChange={(e) => handleChange(e.target.checked)}
                  disabled={disabled}
                />
              }
              label={item.text}
            />
            {hasError && (
              <Typography variant="caption" color="error" display="block">
                {hasError}
              </Typography>
            )}
          </Box>
        );

      case 'date':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              type="date"
              label={item.text}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              error={!!hasError}
              helperText={hasError}
              disabled={disabled}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      case 'datetime-local':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              type="datetime-local"
              label={item.text}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              error={!!hasError}
              helperText={hasError}
              disabled={disabled}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      case 'signature':
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <SignatureCapture
              name={item.linkId}
              label={item.text}
              value={value}
              onChange={(name, signatureData) => handleChange(signatureData)}
              disabled={disabled}
              required={item.required}
              width={item.width || 400}
              height={item.height || 150}
            />
          </Box>
        );

      default:
        return (
          <Box key={item.linkId} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={item.text}
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              error={!!hasError}
              helperText={hasError}
              disabled={disabled}
              size="small"
            />
          </Box>
        );
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {section.text}
      </Typography>
      {section.item?.map(item => renderQuestionItem(item))}
    </Box>
  );
};

export default ProcedureQuestionnaireSection;
